import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetBrands } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import BrandHttpService from '../../../services/http/masterData/brandHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import BrandForm from './form/BrandForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { setBrandsMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import { IBrand } from '../../../@types/model/masterData/brand/brand';
import { IBrandFormValues } from '../../../@types/model/masterData/brand/brandFormValues';
import UserHelper from '../../../services/helper/userHelper';
import BrandModelHelper from '../../../@types/model/masterData/brand/branchModelHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';

const BrandScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const brands = useAppSelector(x => x.masterData.brands);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedBrand, setSelectedBrand] = useState<IBrand | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingBrand, setDeletingBrand] = useState<IBrand | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await BrandHttpService.getBrandData();
                    dispatch(dataSetBrands(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError | undefined;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading brands.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = async (values : IBrandFormValues) => {
        setIsLoading(true);

        try {
            const upsert = BrandModelHelper.createUpsert(values);
            const res = await BrandHttpService.addOrUpdateBrand(upsert);
            const updatedList = upsertArrayElement(brands, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetBrands(updatedList));
            await setBrandsMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedBrand ? 'Brand updated successfully.' : 'Brand added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating brand data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeBrand = async () => {
        if (!deletingBrand) return;
        const updatedBrand = { ...deletingBrand, isActive: false };
        setIsLoading(true);
        try {
            const res = await BrandHttpService.deleteBrand(updatedBrand.id);
            const updatedList = upsertArrayElement(brands, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetBrands(updatedList));
            await setBrandsMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Brand deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting brand.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedBrand(null);
    };

    const edit = (selectedRow : IBrand) => {
        setSelectedBrand(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IBrand) : void => {
        setDeletingBrand(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingBrand(null);
    };

    const deleteButtonTooltip = (row : IBrand) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Brand', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return BrandModelHelper.createFormValues(selectedBrand);
        }
    }, [selectedBrand, isDialogOpen]);

    /*================================================================================================================
    *                                                  Render Methods
    * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IBrand>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Brand', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Brand', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IBrand) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={brands}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedBrand ? 'Edit Brand' : 'Add Brand'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <BrandForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Brand'}
                open={!!deletingBrand}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeBrand}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default BrandScreen;
